import React, { useState, useEffect } from 'react';
import { format, toZonedTime } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';

interface Country {
  code: string;
  name: string;
  timezone: string;
  image: string;
  color: string;
}

const countries: Country[] = [
  { code: 'GB', name: 'UK', timezone: 'Europe/London', image: 'flags/gb.png', color:'#2c2a7c' },
  { code: 'US', name: 'USA', timezone: 'America/New_York', image: 'flags/us.png', color:'#00247d' },
  { code: 'IN', name: 'India', timezone: 'Asia/Kolkata', image: 'flags/in.png', color:'#3d8a41' },
  { code: 'CN', name: 'China', timezone: 'Asia/Shanghai', image: 'flags/cn.png', color:'#ff1212' },
  { code: 'AE', name: 'UAE', timezone: 'Asia/Dubai', image: 'flags/ae.png', color:'#333333' },
  { code: 'SA', name: 'Saudi Arabia', timezone: 'Asia/Riyadh', image: 'flags/sa.png', color:'#129b3e' },
  { code: 'RU', name: 'Russia', timezone: 'Europe/Moscow', image: 'flags/ru.png', color:'#324095' },
  { code: 'JP', name: 'Japan', timezone: 'Asia/Tokyo', image: 'flags/jp.png', color:'#ad1f23' },
  { code: 'CA', name: 'Canada', timezone: 'America/Toronto', image: 'flags/ca.png', color:'#e91e26' },
  { code: 'FR', name: 'France', timezone: 'Europe/Paris', image: 'flags/fr.png', color:'#324095' },
  { code: 'DE', name: 'Germany', timezone: 'Europe/Berlin', image: 'flags/de.png', color:'#fd1312' },
  { code: 'AU', name: 'Australia', timezone: 'Australia/Sydney', image: 'flags/au.png', color:'#2c2a7c' }
];

const saveToStorage = <T,>(key: string, value: T): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

const App: React.FC = () => {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(() => {
    const stored = JSON.parse(localStorage.getItem('selectedCountry') || 'null');
    return stored ? countries.find(c => c.code === stored.code) || null : null;
  });
  
  const [is12HourFormat, setIs12HourFormat] = useState<boolean>(() => {
    return JSON.parse(localStorage.getItem('is12HourFormat') || 'true');
  });

  const [isRoundClock, setIsRoundClock] = useState<boolean>(() => {
    return JSON.parse(localStorage.getItem('isRoundClock') || 'true');
  });

  useEffect(() => {
    if (!selectedCountry) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userCountry = countries.find(c => c.timezone === userTimezone) || 
                         countries.find(c => c.code === 'IN');
      if (userCountry) setSelectedCountry(userCountry);
    }
  }, [selectedCountry]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Save preferences when they change
  useEffect(() => {
    if (selectedCountry) {
      saveToStorage('selectedCountry', {
        code: selectedCountry.code,
        timezone: selectedCountry.timezone
      });
    }
  }, [selectedCountry]);

  useEffect(() => {
    saveToStorage('is12HourFormat', is12HourFormat);
  }, [is12HourFormat]);

  useEffect(() => {
    saveToStorage('isRoundClock', isRoundClock);
  }, [isRoundClock]);

  const getFormattedTime = (timezone: string): { time: string; period: string } => {
    const zonedTime = toZonedTime(currentTime, timezone);
    if (is12HourFormat) {
      const timeStr = format(zonedTime, 'hh : mm : ss', { timeZone: timezone });
      const periodStr = format(zonedTime, 'a', { timeZone: timezone, locale: enUS }).toUpperCase();
      return {
        time: timeStr.replace(/(\d{2}):(\d{2}):(\d{2})/, '$1:$2:$3'),
        period: periodStr
      };
    } else {
      const timeStr = format(zonedTime, 'HH : mm : ss', { timeZone: timezone });
      return {
        time: timeStr.replace(/(\d{2}):(\d{2}):(\d{2})/, '$1:$2:$3'),
        period: ''
      };
    }
  };

  const getFormattedDate = (timezone: string): string => {
    const zonedTime = toZonedTime(currentTime, timezone);
    return format(zonedTime, 'MMM dd EEE', { timeZone: timezone, locale: enUS }).toUpperCase();
  };

  const getCountryPosition = (index: number): { x: number; y: number } => {
    const angle = (index * 30) - 60;
    const radius = 180;
    return {
      x: Math.cos(angle * Math.PI / 180) * radius,
      y: Math.sin(angle * Math.PI / 180) * radius
    };
  };
/* 
  const handleShare = async () => {
    try {
      await navigator.share({
        title: 'Time Ring',
        text: `Current time in ${selectedCountry?.name}: ${getFormattedTime(selectedCountry?.timezone || '')}`,
        url: window.location.href
      });
    } catch (err) {
      navigator.clipboard.writeText(`${selectedCountry?.name}: ${getFormattedTime(selectedCountry?.timezone || '')}`);
      alert('Copied to clipboard!');
    }
  }; */

  return (
    <div className="app mobile-container">
      <div className="header">
        <h1><img src={`/logo.png`} height={100} alt="Time Ring Logo" /></h1>
        <p className="instructions">Tap a flag to check its local time! ⏳🌍</p>
      </div>

      <div className="controls">
        <div className="hour-format-toggle">
          <button 
            onClick={() => setIs12HourFormat(!is12HourFormat)}
            className={`format-button ${is12HourFormat ? 'activee' : ''}`}
          >
           Switch to: {is12HourFormat ? '24H' : '12H'}
          </button>
        </div>
        {/* <button onClick={handleShare} className="share-button">
                 Share Clock
        </button> */}
        <div className="shape-toggle">
          <button 
            onClick={() => setIsRoundClock(!isRoundClock)}
            className="toggle-button"
          >
            {isRoundClock ? '▢ Square Clock' : '◯ Round Clock'}
          </button>
        </div>
      </div>

      <div className={`clock-container ${isRoundClock ? 'round' : 'square'}`}
        style={selectedCountry ? {
          backgroundImage: `url(/${selectedCountry.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          backgroundColor: 'rgba(255, 255, 255, 0.9)' // 30% opacity white overlay
        } : {}}
      >
        {selectedCountry && (
          <div className="center-clock clock-display">
            <div className="country-name" style={{color:`${selectedCountry.color}`}}>{selectedCountry.name}</div>
            <div className="time-container">
              <div className="time">{getFormattedTime(selectedCountry.timezone).time} &nbsp;
              {is12HourFormat && getFormattedTime(selectedCountry.timezone).period}
              </div>
            </div>
            <div className="date">{getFormattedDate(selectedCountry.timezone)}</div>
            <div className="credit">
              <a href="https://www.learn-and-share.in" target='_new'> <img 
                src={`/www.png`} 
                alt="Website"
                title="website" width={20}
              /></a>
              
              <a href="mailto:chirag.khatri@gmail.com"> <img 
                src={`/gmail.png`} 
                alt="Email"
                title="Email Me" width={20}
              /></a>

            <a href="https://www.linkedin.com/in/khatri-chirag/"  target='_new'> <img 
                src={`/linkedin.png`} 
                alt="LinkedIN Profile"
                title="LinkedIN Profile" width={20}
              /></a>

              </div>
          </div>
        )}

        <div className='mob-country-list'>
        {countries.map((country, index) => (
          <div
            key={country.code}
            className={`country ${selectedCountry?.code === country.code ? 'selected' : ''}`}
            style={{
              left: `calc(50% + ${getCountryPosition(index).x}px)`,
              top: `calc(50% + ${getCountryPosition(index).y}px)`
            }}
            onClick={() => setSelectedCountry(country)}
          >
            <img 
                src={`/${country.image}`} 
                alt={country.name}
                title={country.name}
                className="country-flag"
              />
              <span style={{color: selectedCountry?.code === country.code ? country.color : '#000'}}>{country.code}</span>
          </div>
        ))}
        </div>
       
      </div>
      
    </div>
  );
};

export default App;